import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import VoiceInputButton from './components/VoiceInputButton';
import CollapsibleSection from './components/CollapsibleSection';
import { Alert, AlertDescription } from './components/ui/alert';
import { useToast } from './hooks/use-toast';
import { Toaster } from './components/ui/sonner';
import axios from 'axios';
import html2pdf from 'html2pdf.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language Configuration - 8 Indian Languages
const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা' },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी' },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ' }
];

// Static UI translations for all languages
const UI_TRANSLATIONS = {
  en: {
    // Language Selector - Farmer Friendly
    welcomeFarmer: 'Welcome Farmer!',
    welcomeFarmerMulti: 'Welcome Farmer! / किसान भाई आपका स्वागत है!',
    selectLanguage: 'Choose your language to continue',
    selectLanguageMulti: 'Choose your language to continue / अपनी भाषा चुनें',
    welcome: 'Welcome to Free Agro',
    continue: 'Continue',
    loadingTranslation: 'Setting up app in your language...',
    settingUpLanguage: 'Setting up app in your language...',
    
    // Main App
    appTitle: 'Free Agro',
    appTagline: 'AI-powered organic farming solutions. Detect plant diseases, get organic treatments, and schedule fertilizers with advanced agricultural intelligence.',
    getStarted: 'Get Started',
    
    // Contact
    queriesSuggestions: 'For any queries and suggestions',
    contactUsAt: 'Contact us at:',
    
    // Navigation Menu
    agriculturalTools: 'Agricultural Tools',
    analysisHistory: 'Analysis History',
    analysisHistoryDesc: 'Previous plant disease searches and solutions',
    fertiliserSchedule: 'Fertiliser Schedule',
    fertiliserScheduleDesc: 'Create and manage fertiliser applications',
    weatherRecommendation: 'Weather Recommendation',
    weatherRecommendationDesc: 'Agricultural advice based on current weather',
    cropWeatherGuidance: 'Crop Weather Guidance',
    cropWeatherGuidanceDesc: 'Seasonal guidance and climate suitability',
    aiAssistedFeatures: 'AI-Assisted Features',
    aiAssistedFeaturesDesc: 'Chat with AI for farming queries and tips',
    aboutFreeAgro: 'About Free Agro',
    aboutFreeAgroDesc: 'Learn about our mission and vision',
    changeLanguage: 'Change Language',
    close: 'Close',
    
    // Statistics
    totalUsers: 'Total Users',
    totalSearches: 'Total Searches',
    helpfulSolutions: 'Helpful Solutions',
    notHelpful: 'Not Helpful',
    totalReviews: 'Total Reviews',
    communityKnowledge: 'Community-powered agricultural knowledge',
    
    // Plant Analysis Form
    plantDiseaseDetection: 'Plant Disease Detection',
    plantDiseaseDesc: 'Upload a photo of your plant to detect diseases and get organic solutions',
    cropName: 'Crop Name',
    cropNamePlaceholder: 'e.g., Tomato, Rice, Wheat',
    farmerName: 'Farmer Name (Optional)',
    farmerNamePlaceholder: 'Enter your name',
    location: 'Location (Optional)',
    locationPlaceholder: 'e.g., Punjab, Maharashtra',
    plantImage: 'Plant Image',
    analyzePlant: 'Analyze Plant',
    analyzingPlant: 'Analyzing Plant...',
    
    // Buttons & Actions
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    backToHome: 'Back to Home',
    
    // Feedback
    solutionHelpful: 'Was this solution helpful?',
    yesWorked: 'Yes, this worked',
    noDidntWork: "No, this didn't work",
    thankFeedback: 'Thank you for your feedback!',
    yourFeedbackHelps: 'Your vote helps us improve farm solutions globally',
    alreadyVoted: 'Already voted',
    alreadyVotedDesc: 'You have already provided feedback for this solution',
    feedbackFailed: 'Feedback failed',
    feedbackFailedDesc: 'Please try again',
    
    // AI Disclaimer
    aiDisclaimer: '* This is not a 100% accurate solution. This is an AI-generated solution.',
    aiDisclaimerFull: 'This is not a 100% accurate solution. This is an AI-generated solution.',
    
    // Footer
    developedBy: 'Developed by',
    organicFarmingSolutions: 'Organic Farming Solutions',
    
    // Messages
    loading: 'Loading...',
    pleaseWait: 'Please wait...',
    processing: 'Processing...',
    errorOccurred: 'Something went wrong',
    tryAgain: 'Please try again',
    success: 'Success!',
    listening: 'Listening...',
    speakNow: 'Speak now...',
    
    // Error Messages
    missingInfo: 'Missing information',
    enterCropName: 'Please enter crop name',
    selectImage: 'Please select an image',
    selectImageAndCrop: 'Please select an image and enter crop name',
    enterCropAndLocation: 'Please enter crop name and location',
    analysisFailed: 'Analysis failed',
    analysisCompleted: 'Analysis completed!',
    plantAnalyzed: 'Your plant has been analyzed successfully',
    
    // Common Agricultural Terms
    diseaseDetected: 'Disease Detected:',
    organicSolution: 'Organic Solution:',
    diagnosis: 'AI-powered diagnosis and organic treatment recommendations',
    crop: 'Crop',
    confidence: 'Confidence',
  },
  
  // Placeholders for other languages - will be translated in real-time
  hi: {}, bn: {}, mr: {}, te: {}, ta: {}, ml: {}, kn: {}
};

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropName, setCropName] = useState('');
  const [farmerName, setFarmerName] = useState('');
  const [location, setLocation] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [weatherRecs, setWeatherRecs] = useState([]);
  const [currentWeather, setCurrentWeather] = useState('sunny');
  const [cropWeatherGuides, setCropWeatherGuides] = useState([]);
  const [weatherCropMatrix, setWeatherCropMatrix] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [userMessage, setUserMessage] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [feedbackStats, setFeedbackStats] = useState({});
  const [userSession] = useState(() => {
    // Generate or get existing user session ID
    let sessionId = localStorage.getItem('free_agro_user_session');
    if (!sessionId) {
      sessionId = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('free_agro_user_session', sessionId);
    }
    return sessionId;
  });
  const [globalStats, setGlobalStats] = useState({
    total_searches: 0,
    total_helpful_votes: 0,
    total_not_helpful_votes: 0,
    total_reviews: 0
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('analyze');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [showLanguageSelector, setShowLanguageSelector] = useState(true);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const [translationCache, setTranslationCache] = useState({});
  const [guideSearchQuery, setGuideSearchQuery] = useState('');
  const [showFullLegalPolicy, setShowFullLegalPolicy] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  
  const { toast } = useToast();
  const guideContentRef = useRef(null);
  const historyCardRefs = useRef({});

  // Translation helper function with caching and real-time translation
  const t = (key, fallback) => {
    // If English or no language selected, return English version
    if (!selectedLanguage || selectedLanguage === 'en') {
      return UI_TRANSLATIONS.en[key] || fallback || key;
    }
    
    // Check if we have a cached translation
    const cacheKey = `${selectedLanguage}_${key}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }
    
    // Return English as fallback while translation loads
    return UI_TRANSLATIONS.en[key] || fallback || key;
  };

  // Translate text using backend API
  const translateText = async (text, context = '') => {
    if (!selectedLanguage || selectedLanguage === 'en' || !text) {
      return text;
    }
    
    const cacheKey = `${selectedLanguage}_${text.substring(0, 50)}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }
    
    try {
      const response = await axios.post(`${API}/translate`, {
        text: text,
        target_language: selectedLanguage,
        context: context
      });
      
      const translated = response.data.translated_text;
      setTranslationCache(prev => ({ ...prev, [cacheKey]: translated }));
      return translated;
    } catch (error) {
      console.error('Translation error:', error);
      return text; // Fallback to original text
    }
  };

  // Batch translate multiple texts
  const translateBatch = async (texts, context = '') => {
    if (!selectedLanguage || selectedLanguage === 'en' || !texts || texts.length === 0) {
      return texts;
    }
    
    try {
      const response = await axios.post(`${API}/translate-batch`, {
        texts: texts,
        target_language: selectedLanguage,
        context: context
      });
      
      return response.data.translations;
    } catch (error) {
      console.error('Batch translation error:', error);
      return texts; // Fallback to original texts
    }
  };

  // Check for saved language preference on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('free_agro_language');
    if (savedLanguage && LANGUAGES.find(lang => lang.code === savedLanguage)) {
      setSelectedLanguage(savedLanguage);
      setShowLanguageSelector(false);
      preloadUITranslations(savedLanguage);
    }
  }, []);

  // Preload common UI translations
  const preloadUITranslations = async (langCode) => {
    if (langCode === 'en') return;
    
    try {
      const keysToTranslate = Object.keys(UI_TRANSLATIONS.en);
      const textsToTranslate = keysToTranslate.map(key => UI_TRANSLATIONS.en[key]);
      
      const translations = await translateBatch(textsToTranslate, 'Agricultural UI elements');
      
      const newCache = {};
      keysToTranslate.forEach((key, index) => {
        const cacheKey = `${langCode}_${key}`;
        newCache[cacheKey] = translations[index] || UI_TRANSLATIONS.en[key];
      });
      
      setTranslationCache(prev => ({ ...prev, ...newCache }));
    } catch (error) {
      console.error('Preload translations error:', error);
    }
  };

  // Handle language selection
  const handleLanguageSelect = async (languageCode) => {
    setIsLanguageLoading(true);
    setSelectedLanguage(languageCode);
    localStorage.setItem('free_agro_language', languageCode);
    
    // Preload translations for the new language
    if (languageCode !== 'en') {
      await preloadUITranslations(languageCode);
    }
    
    setTimeout(() => {
      setIsLanguageLoading(false);
      setShowLanguageSelector(false);
    }, 1000);
  };

  // Open language selector from menu
  const openLanguageSelector = () => {
    setShowLanguageSelector(true);
    setIsMenuOpen(false);
  };

  // Authentication functions

  // Load previous analyses on component mount
  useEffect(() => {
    loadAnalyses();
    loadSchedules();
    loadWeatherRecommendations();
    loadCropWeatherGuides();
    loadWeatherCropMatrix();
    loadChatSessions();
    loadGlobalStats();
  }, []);

  const loadAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/plant-analyses`);
      setAnalyses(response.data);
      
      // Load feedback stats for each analysis
      for (const analysis of response.data) {
        if (analysis.id) {
          await loadFeedbackStats(analysis.id);
        }
      }
    } catch (error) {
      console.error('Error loading analyses:', error);
    }
  };

  const loadSchedules = async () => {
    try {
      const response = await axios.get(`${API}/fertilizer-schedules`);
      setSchedules(response.data);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const loadWeatherRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/weather-recommendations`);
      setWeatherRecs(response.data);
    } catch (error) {
      console.error('Error loading weather recommendations:', error);
    }
  };

  const loadCropWeatherGuides = async () => {
    try {
      const response = await axios.get(`${API}/crop-weather-guides`);
      setCropWeatherGuides(response.data);
    } catch (error) {
      console.error('Error loading crop weather guides:', error);
    }
  };

  const loadWeatherCropMatrix = async () => {
    try {
      const response = await axios.get(`${API}/weather-crop-compatibility`);
      setWeatherCropMatrix(response.data);
    } catch (error) {
      console.error('Error loading weather crop matrix:', error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
      } else {
        toast({
          title: "Invalid file type",
          description: "Please select an image file",
          variant: "destructive"
        });
      }
    }
  };

  const analyzePlant = async () => {
    if (!selectedFile || !cropName.trim()) {
      toast({
        title: t('missingInfo', 'Missing information'),
        description: t('selectImageAndCrop', 'Please select an image and enter crop name'),
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('crop_name', cropName);
      formData.append('farmer_name', farmerName);
      formData.append('location', location);

      const response = await axios.post(`${API}/analyze-plant`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      let analysisData = response.data;
      
      // Translate the result if not in English
      if (selectedLanguage && selectedLanguage !== 'en') {
        const translatedDisease = await translateText(analysisData.disease_detected, 'Plant disease name');
        const translatedSolution = await translateText(analysisData.organic_solution, 'Organic farming solution with agricultural terminology');
        const translatedCropName = await translateText(analysisData.crop_name, 'Crop/plant name');
        
        analysisData = {
          ...analysisData,
          disease_detected: translatedDisease,
          organic_solution: translatedSolution,
          crop_name: translatedCropName
        };
      }
      
      setAnalysisResult(analysisData);
      loadAnalyses(); // Refresh the list
      
      // Load feedback stats for the new analysis
      if (response.data.id) {
        await loadFeedbackStats(response.data.id);
      }
      
      // Update global stats (search count increased)
      await loadGlobalStats();
      
      toast({
        title: t('analysisCompleted', 'Analysis completed!'),
        description: t('plantAnalyzed', 'Your plant has been analyzed successfully')
      });
    } catch (error) {
      console.error('Error analyzing plant:', error);
      
      // Check if this is a blocked plant error (403 status)
      if (error.response?.status === 403 && error.response?.data?.blocked) {
        const errorData = error.response.data;
        toast({
          title: "❌ " + (errorData.error || "Image Upload Blocked"),
          description: (
            <div className="space-y-2">
              <p className="font-semibold">{errorData.message}</p>
              <p className="text-sm">{errorData.action}</p>
              <p className="text-xs text-gray-400 mt-2">{errorData.legal_notice}</p>
            </div>
          ),
          variant: "destructive",
          duration: 10000 // Show for 10 seconds
        });
      } 
      // Check if this is an "Unable to Process" error (422 status)
      else if (error.response?.status === 422 && error.response?.data?.unable_to_process) {
        const errorData = error.response.data;
        toast({
          title: "❌ " + (errorData.error || "Unable to Process"),
          description: (
            <div className="space-y-2">
              <p className="font-semibold">{errorData.message}</p>
              <p className="text-sm text-gray-600">{errorData.reason}</p>
              <p className="text-sm text-emerald-700 font-medium mt-2">{errorData.action}</p>
            </div>
          ),
          variant: "destructive",
          duration: 8000 // Show for 8 seconds
        });
      } 
      else {
        // Normal error handling
        toast({
          title: t('analysisFailed', 'Analysis failed'),
          description: error.response?.data?.detail || t('tryAgain', 'Please try again'),
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const createFertilizerSchedule = async () => {
    if (!cropName.trim()) {
      toast({
        title: "Missing crop name",
        description: "Please enter a crop name first",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/fertilizer-schedule?crop_name=${encodeURIComponent(cropName)}`);
      loadSchedules(); // Refresh the list
      toast({
        title: "Schedule created!",
        description: "Fertilizer schedule has been generated"
      });
    } catch (error) {
      toast({
        title: "Schedule creation failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
  };

  const createWeatherRecommendation = async () => {
    if (!cropName.trim() || !location.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter crop name and location",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/weather-recommendation?location=${encodeURIComponent(location)}&crop_name=${encodeURIComponent(cropName)}&current_weather=${currentWeather}`);
      loadWeatherRecommendations(); // Refresh the list
      toast({
        title: "Weather recommendation generated!",
        description: "Check the recommendations tab for details"
      });
    } catch (error) {
      toast({
        title: "Recommendation failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
  };

  const createCropWeatherGuide = async () => {
    if (!cropName.trim()) {
      toast({
        title: "Missing crop name",
        description: "Please enter a crop name first",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/crop-weather-guide?crop_name=${encodeURIComponent(cropName)}`);
      loadCropWeatherGuides(); // Refresh the list
      toast({
        title: "Crop weather guide generated!",
        description: "Check the Crop-Weather Guide tab for details"
      });
    } catch (error) {
      toast({
        title: "Guide generation failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
  };

  const loadChatSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat-sessions`);
      setChatSessions(response.data);
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/chat-history/${sessionId}`);
      setChatMessages(response.data);
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendChatMessage = async () => {
    if (!userMessage.trim()) {
      toast({
        title: "Empty message",
        description: "Please enter a message to send to the AI assistant",
        variant: "destructive"
      });
      return;
    }

    setIsChatLoading(true);
    try {
      const chatData = {
        user_message: userMessage,
        farmer_name: farmerName || undefined,
        location: location || undefined,
        session_id: currentSessionId || undefined
      };

      const response = await axios.post(`${API}/chat`, chatData);
      
      // Update session ID if this is a new conversation
      if (!currentSessionId) {
        setCurrentSessionId(response.data.session_id);
      }
      
      // Add the new message to current chat
      setChatMessages(prev => [...prev, response.data]);
      
      // Clear input
      setUserMessage('');
      
      // Refresh sessions list
      loadChatSessions();
      
      toast({
        title: "Message sent!",
        description: "Dr. AgroBot has responded to your question"
      });
    } catch (error) {
      toast({
        title: "Message failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const startNewChat = () => {
    setCurrentSessionId(null);
    setChatMessages([]);
    setUserMessage('');
  };

  const loadSession = (sessionId) => {
    setCurrentSessionId(sessionId);
    loadChatHistory(sessionId);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const navigateToTab = (tabName) => {
    setActiveTab(tabName);
    setIsMenuOpen(false); // Ensure menu closes after navigation
  };

  const loadFeedbackStats = async (analysisId) => {
    try {
      const response = await axios.get(`${API}/solution-feedback/${analysisId}?user_session=${userSession}`);
      setFeedbackStats(prev => ({
        ...prev,
        [analysisId]: response.data
      }));
    } catch (error) {
      console.error('Error loading feedback stats:', error);
    }
  };

  const loadGlobalStats = async () => {
    try {
      const response = await axios.get(`${API}/global-stats`);
      setGlobalStats(response.data);
    } catch (error) {
      console.error('Error loading global stats:', error);
    }
  };

  const submitFeedback = async (analysisId, feedbackType) => {
    try {
      const feedbackData = {
        analysis_id: analysisId,
        feedback_type: feedbackType,
        user_session: userSession,
        farmer_name: farmerName || undefined,
        location: location || undefined
      };

      await axios.post(`${API}/solution-feedback`, feedbackData);
      
      // Reload feedback stats
      await loadFeedbackStats(analysisId);
      
      // Reload global stats to update the display
      await loadGlobalStats();
      
      toast({
        title: t('thankFeedback', 'Thank you for your feedback!'),
        description: t('yourFeedbackHelps', 'Your vote helps us improve farm solutions globally')
      });
    } catch (error) {
      if (error.response?.status === 400) {
        toast({
          title: t('alreadyVoted', 'Already voted'),
          description: t('alreadyVotedDesc', 'You have already provided feedback for this solution'),
          variant: "destructive"
        });
      } else {
        toast({
          title: t('feedbackFailed', 'Feedback failed'),
          description: t('feedbackFailedDesc', 'Please try again'),
          variant: "destructive"
        });
      }
    }
  };

  // Organic Farming Guide - Search helper function
  const searchInText = (text, query) => {
    if (!query) return true;
    return text.toLowerCase().includes(query.toLowerCase());
  };

  // Toggle history item expansion
  const toggleHistoryExpand = (analysisId) => {
    setExpandedHistoryId(prev => prev === analysisId ? null : analysisId);
    
    // Scroll to the card after a short delay to allow expansion animation
    setTimeout(() => {
      if (historyCardRefs.current[analysisId]) {
        historyCardRefs.current[analysisId].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    }, 100);
  };

  // Check if any section matches the search query
  const shouldShowSection = (searchableText) => {
    if (!guideSearchQuery) return true;
    return searchInText(searchableText, guideSearchQuery);
  };

  // Check if at least one section should be shown
  const shouldShowAnySection = () => {
    if (!guideSearchQuery) return true;
    
    const sections = [
      'small scale organic farming site soil planning crop compost rotation biodiversity pest disease fertilizer monitoring',
      'large scale organic farming assessment transition infrastructure planning pest disease management input production monitoring market',
      'essential organic materials compost jeevamrut panchagavya neem oil mulch',
      'tools equipment broadfork wheel hoe harvest knives silage tarps watering cans drip lines tractor rotavator compost turner irrigation digital mapping',
      'key organic solutions recipes jeevamrut panchagavya neemastra neem oil hot pepper caterpillar beetle',
      'benefits support organic farming soil fertility growth yield pest disease management sustainability'
    ];
    
    return sections.some(section => searchInText(section, guideSearchQuery));
  };

  // Organic Farming Guide - Print functionality
  const handlePrintGuide = () => {
    window.print();
  };

  // Organic Farming Guide - Download as PDF functionality
  const handleDownloadPDF = async () => {
    if (!guideContentRef.current) {
      toast({
        title: "Error",
        description: "Guide content not found",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Generating PDF...",
      description: "Please wait while we prepare your guide"
    });

    try {
      const element = guideContentRef.current;
      const opt = {
        margin: 10,
        filename: 'Free_Agro_Organic_Farming_Guide.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };

      await html2pdf().from(element).set(opt).save();
      
      toast({
        title: "Success!",
        description: "PDF downloaded successfully"
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast({
        title: "Download failed",
        description: "Please try again",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
      {/* Language Selector Overlay - Full Screen Modal */}
      {showLanguageSelector && (
        <div className="fixed inset-0 bg-white z-[100] flex items-center justify-center language-selector-overlay">
          <div className="max-w-5xl w-full px-6">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="mb-6">
                <div className="text-7xl mb-4">🌱</div>
                <h1 className="text-6xl font-bold text-emerald-700 mb-4 font-serif">
                  Free Agro
                </h1>
              </div>
              <div className="text-3xl font-bold text-emerald-600 mb-6">
                {t('welcomeFarmerMulti', 'Welcome Farmer! / किसान भाई आपका स्वागत है!')}
              </div>
              <div className="w-24 h-1 bg-emerald-600 mx-auto rounded-full mb-8"></div>
              <h2 className="text-2xl font-semibold text-gray-700">
                {t('selectLanguageMulti', 'Choose your language to continue / अपनी भाषा चुनें')}
              </h2>
            </div>

            {/* Language Loading State */}
            {isLanguageLoading && (
              <div className="text-center mb-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-emerald-600"></div>
                <p className="text-emerald-600 mt-4 text-lg font-medium">
                  {t('settingUpLanguage', 'Setting up app in your language...')}
                </p>
                <p className="text-gray-500 mt-2 text-sm">
                  आपकी भाषा में ऐप सेट कर रहे हैं...
                </p>
              </div>
            )}

            {/* Language Grid */}
            {!isLanguageLoading && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
                {LANGUAGES.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageSelect(language.code)}
                    className={`language-button p-6 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                      selectedLanguage === language.code
                        ? 'bg-emerald-600 text-white border-emerald-700 shadow-xl language-button-selected'
                        : 'bg-white text-gray-800 border-emerald-200 hover:border-emerald-400 hover:shadow-lg'
                    }`}
                    data-testid={`lang-${language.code}`}
                  >
                    <div className="text-center">
                      <div className="text-3xl font-bold mb-2">{language.nativeName}</div>
                      <div className="text-sm opacity-80">{language.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Continue Button */}
            {selectedLanguage && !isLanguageLoading && (
              <div className="text-center mt-12">
                <Button
                  onClick={() => setShowLanguageSelector(false)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-12 py-4 text-xl font-semibold rounded-full shadow-xl transition-all duration-300 hover:scale-105"
                  data-testid="continue-btn"
                >
                  {t('continue', 'Continue')}
                </Button>
              </div>
            )}

            {/* Footer */}
            <div className="text-center mt-16 text-gray-500">
              <div className="text-sm">🌱 Free Agro - AI-powered organic farming solutions</div>
            </div>
          </div>
        </div>
      )}

      {/* Main App Content */}
      {!showLanguageSelector && (
        <>
      {/* Hamburger Menu Button - Small/Compact */}
      <button
        onClick={toggleMenu}
        className="fixed top-3 left-3 z-50 bg-white/90 backdrop-blur-sm p-2 rounded-md shadow-md border border-emerald-200 hover:bg-emerald-50 transition-all duration-300"
        data-testid="hamburger-menu-btn"
        style={{ width: '28px', height: '28px' }}
      >
        <div className="flex flex-col justify-center items-center space-y-0.5">
          <span className="block w-4 h-0.5 bg-emerald-600"></span>
          <span className="block w-4 h-0.5 bg-emerald-600"></span>
          <span className="block w-4 h-0.5 bg-emerald-600"></span>
        </div>
      </button>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={closeMenu}
          data-testid="menu-overlay"
        />
      )}

      {/* Slide-out Menu */}
      <div 
        className={`fixed top-0 left-0 h-full w-80 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="slide-out-menu"
        style={{ maxHeight: '90vh' }}
      >
        {/* Fixed Menu Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">🌱 Agricultural Tools</h2>
            <button
              onClick={closeMenu}
              className="p-2 hover:bg-white/20 rounded-full transition-colors duration-200"
              data-testid="menu-close-btn"
            >
              <span className="text-xl">×</span>
            </button>
          </div>
        </div>

        {/* Scrollable Menu Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-emerald-400 scrollbar-track-gray-100">
          <div className="p-4 pt-6">
            <nav className="space-y-3">
              <button
                onClick={() => navigateToTab('history')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-analysis-history"
              >
                <span className="text-xl">📊</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('analysisHistory', 'Analysis History')}</div>
                  <div className="text-xs text-gray-600">{t('analysisHistoryDesc', 'Previous plant disease searches and solutions')}</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('schedule')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-fertiliser-schedule"
              >
                <span className="text-xl">📅</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('fertiliserSchedule', 'Fertiliser Schedule')}</div>
                  <div className="text-xs text-gray-600">{t('fertiliserScheduleDesc', 'Create and manage fertiliser applications')}</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('weather')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-weather-recommendation"
              >
                <span className="text-xl">🌤️</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('weatherRecommendation', 'Weather Recommendation')}</div>
                  <div className="text-xs text-gray-600">{t('weatherRecommendationDesc', 'Agricultural advice based on current weather')}</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('crop-weather')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-crop-weather-guidance"
              >
                <span className="text-xl">🌦️</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('cropWeatherGuidance', 'Crop Weather Guidance')}</div>
                  <div className="text-xs text-gray-600">{t('cropWeatherGuidanceDesc', 'Seasonal guidance and climate suitability')}</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('ai-assistant')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-ai-assisted-features"
              >
                <span className="text-xl">🤖</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('aiAssistedFeatures', 'AI-Assisted Features')}</div>
                  <div className="text-xs text-gray-600">{t('aiAssistedFeaturesDesc', 'Chat with AI for farming queries and tips')}</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('organic-guide')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-organic-guide"
              >
                <span className="text-xl">📖</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">Organic Farming Guide</div>
                  <div className="text-xs text-gray-600">जैविक खेती गाइड / Complete farming guide</div>
                </div>
              </button>

              <button
                onClick={() => navigateToTab('about')}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left"
                data-testid="menu-about"
              >
                <span className="text-xl">🌱</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('aboutFreeAgro', 'About Free Agro')}</div>
                  <div className="text-xs text-gray-600">{t('aboutFreeAgroDesc', 'Learn about our mission and vision')}</div>
                </div>
              </button>

              <button
                onClick={openLanguageSelector}
                className="menu-item w-full flex items-center space-x-3 rounded-lg text-left border-t pt-4 mt-4"
                data-testid="menu-change-language"
              >
                <span className="text-xl">🌐</span>
                <div className="flex-1">
                  <div className="font-semibold text-gray-800 text-sm">{t('changeLanguage', 'Change Language')}</div>
                  <div className="text-xs text-gray-600">
                    {selectedLanguage && LANGUAGES.find(lang => lang.code === selectedLanguage)?.nativeName}
                  </div>
                </div>
              </button>
            </nav>

            {/* Menu Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <div className="mb-2">🌱 Organic Farming Solutions</div>
                <div className="text-xs">Powered by AI for sustainable agriculture</div>
              </div>
            </div>
            
            {/* Bottom padding for better scroll experience */}
            <div className="h-4"></div>
          </div>
        </div>
      </div>

      {/* Creator Attribution - Bottom Left */}
      <div 
        className="creator-attribution-bottom fixed bottom-4 left-4 z-30 bg-white/85 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-emerald-200 hover:bg-white/95 transition-all duration-300"
        data-testid="creator-attribution"
      >
        <div className="text-left">
          <div className="text-xs text-gray-500 font-normal leading-tight">Developed by</div>
          <div className="text-sm text-gray-700 font-medium leading-tight mt-1">Mithilesh Sai Swaroop Gamini</div>
        </div>
      </div>
      
      {/* Global Statistics Display */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-emerald-100 py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center space-x-8 text-sm">
            <div className="flex items-center space-x-2" data-testid="global-total-searches">
              <span className="text-emerald-600 font-medium">🔍</span>
              <span className="text-gray-700">{t('totalSearches', 'Total Searches')}:</span>
              <span className="font-bold text-emerald-700">{globalStats.total_searches.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-2" data-testid="global-helpful-votes">
              <span className="text-green-600 font-medium">✅</span>
              <span className="text-gray-700">{t('helpfulSolutions', 'Helpful Solutions')}:</span>
              <span className="font-bold text-green-700">{globalStats.total_helpful_votes.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-2" data-testid="global-not-helpful-votes">
              <span className="text-red-600 font-medium">❌</span>
              <span className="text-gray-700">{t('notHelpful', 'Not Helpful')}:</span>
              <span className="font-bold text-red-700">{globalStats.total_not_helpful_votes.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500" data-testid="global-community-stats">
              <span>•</span>
              <span>{t('communityKnowledge', 'Community-powered agricultural knowledge')}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sticky Legal Notice Banner */}
      <div className="sticky top-0 z-30 bg-red-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-2 flex-1">
              <span className="text-xl flex-shrink-0">⚠️</span>
              <p className="text-sm font-medium">
                <span className="font-bold">Legal Notice:</span> Free Agro cannot diagnose plants restricted under 
                Indian law (Cannabis, Opium poppy, Coca plant, etc.).
              </p>
            </div>
            <button
              onClick={() => setShowFullLegalPolicy(!showFullLegalPolicy)}
              className="text-xs font-semibold underline hover:text-red-100 transition-colors whitespace-nowrap"
            >
              {showFullLegalPolicy ? 'Hide policy ↑' : 'Read full policy →'}
            </button>
          </div>
          
          {/* Expandable Full Policy */}
          {showFullLegalPolicy && (
            <div className="mt-3 pt-3 border-t border-red-500 text-sm animate-in slide-in-from-top duration-300">
              <div className="space-y-2">
                <p className="font-semibold">
                  Free Agro cannot and will not provide diagnosis for plants that are 
                  illegal or restricted under Indian law, including but not limited to:
                </p>
                <ul className="list-disc ml-6 space-y-1 text-xs">
                  <li>Cannabis (marijuana, weed, ganja, bhang, charas, hemp)</li>
                  <li>Opium Poppy (Papaver somniferum, afeem)</li>
                  <li>Coca Plant (Erythroxylum coca, cocaine)</li>
                  <li>Peyote (Lophophora, mescaline cactus)</li>
                  <li>Magic Mushrooms (Psilocybin mushrooms)</li>
                  <li>Kratom (Mitragyna speciosa)</li>
                  <li>Datura (Jimsonweed, Devil's Trumpet, Dhatura)</li>
                  <li>Belladonna (Deadly Nightshade, Atropa belladonna)</li>
                </ul>
                <p className="font-bold text-red-100 mt-2">
                  ⚖️ Users attempting to upload restricted plant images may be reported 
                  to authorities as per applicable Indian laws.
                </p>
                <div className="mt-2 pt-2 border-t border-red-500">
                  <p className="text-xs">
                    <span className="font-semibold">Complies with:</span> Narcotic Drugs and Psychotropic Substances Act 1985, 
                    State agricultural regulations, All applicable Indian laws.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Hero Section */}
      <div className="relative h-96 bg-cover bg-center" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1560493676-04071c5f467b')`
      }}>
        <div className="absolute inset-0 flex items-center justify-center text-center">
          <div className="max-w-4xl px-6">
            <h1 className="text-6xl font-bold text-white mb-6 font-serif tracking-tight">
              {t('appTitle', 'Free Agro')}
            </h1>
            <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('appTagline', 'AI-powered organic farming solutions. Detect plant diseases, get organic treatments, and schedule fertilizers with advanced agricultural intelligence.')}
            </p>
            
            {/* Contact Information */}
            <div className="contact-info-section text-center mb-8" data-testid="contact-info">
              <div className="text-emerald-100 text-base font-normal max-w-2xl mx-auto space-y-3">
                {/* Line 1 */}
                <div className="text-emerald-100">{t('queriesSuggestions', 'For any queries and suggestions')}</div>
                
                {/* Line 2 */}
                <div className="flex items-center justify-center flex-wrap">
                  <span className="text-lg mr-2">📧</span>
                  <span className="font-semibold text-emerald-200 mr-2">{t('contactUsAt', 'Contact us at:')}</span>
                  <a 
                    href="mailto:orgfreeagro@gmail.com" 
                    className="text-emerald-200 font-medium underline hover:text-white transition-colors duration-300 hover:scale-105"
                    data-testid="email-link"
                  >
                    orgfreeagro@gmail.com
                  </a>
                </div>
              </div>
            </div>
            
            <Button 
              size="lg" 
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg font-medium rounded-full shadow-xl transition-all duration-300 hover:scale-105"
              data-testid="get-started-btn"
            >
              {t('getStarted', 'Get Started')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12 max-w-7xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Hide tab list when not on analyze tab - menu navigation handles other tabs */}
          {activeTab === 'analyze' && (
            <div className="text-center mb-8">
              <Button
                onClick={() => setActiveTab('analyze')}
                className="bg-emerald-600 text-white px-6 py-2 rounded-lg font-medium"
                data-testid="main-analysis-tab"
              >
                🌱 Plant Analysis - Main Feature
              </Button>
            </div>
          )}
          
          {/* Navigation breadcrumb for other tabs */}
          {activeTab !== 'analyze' && (
            <div className="flex items-center justify-between mb-8 bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setActiveTab('analyze')}
                  className="text-emerald-600 hover:text-emerald-700 font-medium"
                  data-testid="back-to-main"
                >
                  ← Back to Plant Analysis
                </button>
              </div>
              <div className="text-emerald-700 font-semibold capitalize">
                {activeTab === 'history' && '📊 Analysis History'}
                {activeTab === 'schedule' && '📅 Fertiliser Schedule'}
                {activeTab === 'weather' && '🌤️ Weather Recommendations'}
                {activeTab === 'crop-weather' && '🌦️ Crop Weather Guidance'}
                {activeTab === 'ai-assistant' && '🤖 AI-Assisted Features'}
                {activeTab === 'about' && '🌱 About Free Agro'}
              </div>
            </div>
          )}

          {/* Plant Analysis Tab */}
          <TabsContent value="analyze" className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Form */}
              <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
                <CardHeader className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-t-lg">
                  <CardTitle className="text-2xl font-bold">{t('plantDiseaseDetection', 'Plant Disease Detection')}</CardTitle>
                  <CardDescription className="text-emerald-100">
                    {t('plantDiseaseDesc', 'Upload a photo of your plant to detect diseases and get organic solutions')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="crop-name" className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('cropName', 'Crop Name')} *
                      </Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="crop-name"
                          type="text"
                          placeholder={t('cropNamePlaceholder', 'e.g., Tomato, Rice, Wheat')}
                          value={cropName}
                          onChange={(e) => setCropName(e.target.value)}
                          className="w-full border-gray-300 focus:border-emerald-500 rounded-lg"
                          data-testid="crop-name-input"
                        />
                        <VoiceInputButton
                          language={selectedLanguage || 'en'}
                          onTranscript={(text) => setCropName(text)}
                          placeholder={t('listening', 'Listening...')}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="farmer-name" className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('farmerName', 'Farmer Name (Optional)')}
                      </Label>
                      <Input
                        id="farmer-name"
                        type="text"
                        placeholder={t('farmerNamePlaceholder', 'Enter your name')}
                        value={farmerName}
                        onChange={(e) => setFarmerName(e.target.value)}
                        className="w-full border-gray-300 focus:border-emerald-500 rounded-lg"
                        data-testid="farmer-name-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="location" className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('location', 'Location (Optional)')}
                      </Label>
                      <Input
                        id="location"
                        type="text"
                        placeholder={t('locationPlaceholder', 'e.g., Punjab, Maharashtra')}
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="w-full border-gray-300 focus:border-emerald-500 rounded-lg"
                        data-testid="location-input"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="plant-image" className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('plantImage', 'Plant Image')} *
                      </Label>
                      <Input
                        id="plant-image"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full border-gray-300 focus:border-emerald-500 rounded-lg file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                        data-testid="plant-image-input"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    onClick={analyzePlant} 
                    disabled={loading} 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-medium text-lg transition-all duration-300 hover:scale-105 disabled:hover:scale-100"
                    data-testid="analyze-plant-btn"
                  >
                    {loading ? t('analyzingPlant', 'Analyzing Plant...') : `🔍 ${t('analyzePlant', 'Analyze Plant')}`}
                  </Button>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Button 
                      onClick={createFertilizerSchedule}
                      variant="outline"
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                      data-testid="create-schedule-btn"
                    >
                      📅 Create Fertilizer Schedule
                    </Button>
                    
                    <Button 
                      onClick={createWeatherRecommendation}
                      variant="outline"
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                      data-testid="weather-recommendation-btn"
                    >
                      🌤️ Weather Recommendation
                    </Button>
                    
                    <Button 
                      onClick={createCropWeatherGuide}
                      variant="outline"
                      className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                      data-testid="crop-weather-guide-btn"
                    >
                      🌦️ Crop Weather Guide
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Analysis Result */}
              {analysisResult && (
                <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
                  <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
                    <CardTitle className="text-xl font-bold">Analysis Results</CardTitle>
                    <CardDescription className="text-green-100">
                      AI-powered diagnosis and organic treatment recommendations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Crop: {analysisResult.crop_name}</h3>
                      <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600">
                        Confidence: {analysisResult.confidence_score}%
                      </Badge>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">🦠 Disease Detected:</h4>
                      <p className="text-gray-700 bg-red-50 p-3 rounded-lg border border-red-200">
                        {analysisResult.disease_detected}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">🌿 Organic Solution:</h4>
                      <div className="text-gray-700 bg-green-50 p-4 rounded-lg border border-green-200 max-h-64 overflow-y-auto">
                        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                          {analysisResult.organic_solution}
                        </pre>
                      </div>
                      
                      {/* AI Disclaimer */}
                      <div className="mt-2 mb-2 py-1 px-2 bg-white border border-gray-300 rounded text-center" data-testid="ai-disclaimer">
                        <div className="text-black font-normal text-sm">
                          <span>{t('aiDisclaimer', '* This is not a 100% accurate solution. This is an AI-generated solution.')}</span>
                        </div>
                      </div>
                      
                      {/* Feedback Section */}
                      <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200 text-center">
                        <h5 className="font-semibold text-gray-800 mb-3">{t('solutionHelpful', 'Was this solution helpful?')}</h5>
                        
                        {(() => {
                          const stats = feedbackStats[analysisResult.id];
                          const userHasVoted = stats?.user_has_voted || false;
                          const userVoteType = stats?.user_vote_type;
                          
                          return (
                            <div className="flex justify-center space-x-4 mb-3">
                              <Button
                                onClick={() => submitFeedback(analysisResult.id, 'helpful')}
                                disabled={userHasVoted}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                                  userVoteType === 'helpful' 
                                    ? 'bg-green-600 text-white border-2 border-green-700' 
                                    : userHasVoted 
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                                }`}
                                data-testid="feedback-helpful-btn"
                              >
                                <span>✅</span>
                                <span>{t('yesWorked', 'Yes, this worked')}</span>
                                {stats && (
                                  <Badge variant="outline" className="ml-1 bg-white text-green-700">
                                    {stats.helpful_count}
                                  </Badge>
                                )}
                              </Button>
                              
                              <Button
                                onClick={() => submitFeedback(analysisResult.id, 'not_helpful')}
                                disabled={userHasVoted}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                                  userVoteType === 'not_helpful' 
                                    ? 'bg-red-600 text-white border-2 border-red-700' 
                                    : userHasVoted 
                                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                      : 'bg-red-50 text-red-700 border border-red-300 hover:bg-red-100'
                                }`}
                                data-testid="feedback-not-helpful-btn"
                              >
                                <span>❌</span>
                                <span>{t('noDidntWork', "No, this didn't work")}</span>
                                {stats && (
                                  <Badge variant="outline" className="ml-1 bg-white text-red-700">
                                    {stats.not_helpful_count}
                                  </Badge>
                                )}
                              </Button>
                            </div>
                          );
                        })()}
                        
                        {(() => {
                          const stats = feedbackStats[analysisResult.id];
                          if (stats?.user_has_voted) {
                            return (
                              <div className="text-sm text-green-600 font-medium">
                                ✨ Thank you for your feedback!
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        {/* Global Statistics Display */}
                        <div className="text-xs text-gray-500 mt-3 border-t pt-2" data-testid="solution-global-stats">
                          <div className="flex justify-center space-x-4">
                            <span>Total Reviews: <strong>{globalStats.total_reviews.toLocaleString()}</strong></span>
                            <span className="text-green-600">✅ Helpful: <strong>{globalStats.total_helpful_votes.toLocaleString()}</strong></span>
                            <span className="text-red-600">❌ Not Helpful: <strong>{globalStats.total_not_helpful_votes.toLocaleString()}</strong></span>
                          </div>
                          {globalStats.total_reviews > 0 && (
                            <div className="text-center mt-1 text-emerald-600">
                              {Math.round((globalStats.total_helpful_votes / globalStats.total_reviews) * 100)}% of farmers found our solutions helpful
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Analysis History Tab */}
          <TabsContent value="history" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Previous Plant Analyses</h2>
              {analyses.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500 text-lg">No analyses found. Start by analyzing your first plant!</p>
                  </CardContent>
                </Card>
              ) : (
                analyses.map((analysis, index) => {
                  const isExpanded = expandedHistoryId === analysis.id;
                  
                  return (
                    <Card 
                      key={analysis.id}
                      ref={el => historyCardRefs.current[analysis.id] = el}
                      className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-white/90 backdrop-blur-sm cursor-pointer"
                      data-testid={`analysis-card-${index}`}
                      onClick={() => toggleHistoryExpand(analysis.id)}
                    >
                      <CardHeader className="bg-gradient-to-r from-emerald-500 to-green-500 text-white">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <CardTitle className="text-xl font-bold flex items-center gap-2">
                              <span>{analysis.crop_name}</span>
                              <span className="text-sm font-normal opacity-90">
                                {isExpanded ? '▼' : '▶'}
                              </span>
                            </CardTitle>
                            <CardDescription className="text-emerald-100">
                              📅 {new Date(analysis.created_at).toLocaleString()}
                              {analysis.farmer_name && <span> • 👨‍🌾 {analysis.farmer_name}</span>}
                              {analysis.location && <span> • 📍 {analysis.location}</span>}
                            </CardDescription>
                          </div>
                          <Badge className="bg-white text-emerald-600 font-medium">
                            {analysis.confidence_score}% confidence
                          </Badge>
                        </div>
                        {!isExpanded && (
                          <div className="mt-2 text-sm text-emerald-100 opacity-90">
                            Click to view full details →
                          </div>
                        )}
                      </CardHeader>
                      
                      {isExpanded && (
                        <CardContent className="p-6" onClick={(e) => e.stopPropagation()}>
                          <div className="space-y-4">
                            {/* Search Details */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <h4 className="font-semibold text-gray-900 mb-2">📋 Search Details:</h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-gray-600">Crop:</span>
                                  <span className="ml-2 font-medium text-gray-900">{analysis.crop_name}</span>
                                </div>
                                {analysis.farmer_name && (
                                  <div>
                                    <span className="text-gray-600">Farmer:</span>
                                    <span className="ml-2 font-medium text-gray-900">{analysis.farmer_name}</span>
                                  </div>
                                )}
                                {analysis.location && (
                                  <div>
                                    <span className="text-gray-600">Location:</span>
                                    <span className="ml-2 font-medium text-gray-900">{analysis.location}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-gray-600">Analyzed:</span>
                                  <span className="ml-2 font-medium text-gray-900">
                                    {new Date(analysis.created_at).toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-600">Confidence:</span>
                                  <span className="ml-2 font-medium text-gray-900">{analysis.confidence_score}%</span>
                                </div>
                              </div>
                            </div>

                            {/* Disease Detection */}
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">🦠 Disease Detected:</h4>
                              <p className="text-gray-700 bg-red-50 p-3 rounded-lg">{analysis.disease_detected}</p>
                            </div>

                            {/* Organic Solution */}
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">🌿 Complete Organic Solution:</h4>
                              <div className="text-gray-700 bg-green-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                                  {analysis.organic_solution}
                                </pre>
                              </div>
                              
                              {/* AI Disclaimer */}
                              <div className="mt-2 mb-2 py-1 px-2 bg-white border border-gray-300 rounded text-center">
                                <div className="text-black font-normal text-xs">
                                  <span>{t('aiDisclaimer', '* This is not a 100% accurate solution. This is an AI-generated solution.')}</span>
                                </div>
                              </div>
                              
                              {/* Feedback Section */}
                              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200 text-center">
                                <h6 className="font-medium text-gray-700 text-sm mb-2">Was this solution helpful?</h6>
                                
                                {(() => {
                                  const stats = feedbackStats[analysis.id];
                                  const userHasVoted = stats?.user_has_voted || false;
                                  const userVoteType = stats?.user_vote_type;
                                  
                                  return (
                                    <div className="flex justify-center space-x-3 mb-2">
                                      <Button
                                        size="sm"
                                        onClick={() => submitFeedback(analysis.id, 'helpful')}
                                        disabled={userHasVoted}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-xs transition-all ${
                                          userVoteType === 'helpful' 
                                            ? 'bg-green-600 text-white border border-green-700' 
                                            : userHasVoted 
                                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                              : 'bg-green-50 text-green-700 border border-green-300 hover:bg-green-100'
                                        }`}
                                        data-testid={`history-feedback-helpful-${index}`}
                                      >
                                        <span>✅</span>
                                        <span>Yes</span>
                                        {stats && (
                                          <span className="text-xs">({stats.helpful_count})</span>
                                        )}
                                      </Button>
                                      
                                      <Button
                                        size="sm"
                                        onClick={() => submitFeedback(analysis.id, 'not_helpful')}
                                        disabled={userHasVoted}
                                        className={`flex items-center space-x-1 px-3 py-1 rounded-md text-xs transition-all ${
                                          userVoteType === 'not_helpful' 
                                            ? 'bg-red-600 text-white border border-red-700' 
                                            : userHasVoted 
                                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                              : 'bg-red-50 text-red-700 border border-red-300 hover:bg-red-100'
                                        }`}
                                        data-testid={`history-feedback-not-helpful-${index}`}
                                      >
                                        <span>❌</span>
                                        <span>No</span>
                                        {stats && (
                                          <span className="text-xs">({stats.not_helpful_count})</span>
                                        )}
                                      </Button>
                                    </div>
                                  );
                                })()}
                                
                                {(() => {
                                  const stats = feedbackStats[analysis.id];
                                  if (stats?.user_has_voted) {
                                    return (
                                      <div className="text-xs text-green-600 font-medium mb-1">
                                        Thank you for your feedback!
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                
                                {/* Global Statistics */}
                                <div className="text-xs text-gray-500 border-t pt-1" data-testid={`history-global-stats-${index}`}>
                                  <div className="flex justify-center space-x-2">
                                    <span>Reviews: <strong>{globalStats.total_reviews.toLocaleString()}</strong></span>
                                    <span className="text-green-600">✅ {globalStats.total_helpful_votes.toLocaleString()}</span>
                                    <span className="text-red-600">❌ {globalStats.total_not_helpful_votes.toLocaleString()}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Close Button */}
                            <div className="text-center pt-2">
                              <button
                                onClick={() => setExpandedHistoryId(null)}
                                className="text-sm text-gray-600 hover:text-gray-900 underline"
                              >
                                Click to collapse ▲
                              </button>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Fertilizer Schedule Tab */}
          <TabsContent value="schedule" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Fertilizer Schedules</h2>
              {schedules.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500 text-lg">No schedules found. Create your first fertilizer schedule!</p>
                  </CardContent>
                </Card>
              ) : (
                schedules.map((schedule, index) => (
                  <Card 
                    key={schedule.id} 
                    className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-white/90 backdrop-blur-sm"
                    data-testid={`schedule-card-${index}`}
                  >
                    <CardHeader className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl font-bold">{schedule.crop_name}</CardTitle>
                          <CardDescription className="text-amber-100">
                            {schedule.fertilizer_type}
                          </CardDescription>
                        </div>
                        <Badge className="bg-white text-amber-600 font-medium">
                          Next: {schedule.next_application}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">📅 Application Date:</h4>
                          <p className="text-gray-700 bg-blue-50 p-3 rounded-lg">{schedule.application_date}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">🧪 Organic Ingredients:</h4>
                          <div className="flex flex-wrap gap-2">
                            {schedule.organic_ingredients.map((ingredient, idx) => (
                              <Badge key={idx} variant="outline" className="border-green-500 text-green-600">
                                {ingredient}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">📋 Instructions:</h4>
                          <div className="text-gray-700 bg-green-50 p-4 rounded-lg max-h-48 overflow-y-auto">
                            <pre className="whitespace-pre-wrap font-sans text-sm">
                              {schedule.instructions?.substring(0, 300)}...
                            </pre>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Weather Recommendations Tab */}
          <TabsContent value="weather" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Weather-Based Recommendations</h2>
              
              <Card className="shadow-lg bg-white/90 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle>Current Weather Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex space-x-4">
                    <select 
                      value={currentWeather} 
                      onChange={(e) => setCurrentWeather(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-2 focus:border-emerald-500"
                      data-testid="weather-select"
                    >
                      <option value="sunny">☀️ Sunny</option>
                      <option value="cloudy">☁️ Cloudy</option>
                      <option value="rainy">🌧️ Rainy</option>
                      <option value="stormy">⛈️ Stormy</option>
                      <option value="windy">💨 Windy</option>
                    </select>
                  </div>
                </CardContent>
              </Card>
              
              {weatherRecs.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500 text-lg">No weather recommendations found. Generate your first recommendation!</p>
                  </CardContent>
                </Card>
              ) : (
                weatherRecs.map((rec, index) => (
                  <Card 
                    key={rec.id} 
                    className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-white/90 backdrop-blur-sm"
                    data-testid={`weather-card-${index}`}
                  >
                    <CardHeader className={`text-white ${
                      rec.action_required 
                        ? 'bg-gradient-to-r from-red-600 to-orange-600' 
                        : 'bg-gradient-to-r from-blue-600 to-cyan-600'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-xl font-bold">{rec.crop_name}</CardTitle>
                          <CardDescription className="text-blue-100">
                            📍 {rec.location} • {rec.current_weather}
                          </CardDescription>
                        </div>
                        {rec.action_required && (
                          <Badge className="bg-white text-red-600 font-medium animate-pulse">
                            ⚠️ Action Required
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">🌤️ Weather Recommendation:</h4>
                          <div className="text-gray-700 bg-blue-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                              {rec.recommendation}
                            </pre>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          Generated on: {new Date(rec.created_at).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Crop-Weather Guide Tab */}
          <TabsContent value="crop-weather" className="space-y-6">
            <div className="grid grid-cols-1 gap-6">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Crop-Weather Compatibility Guide</h2>
              
              {/* Weather-Crop Compatibility Matrix */}
              {weatherCropMatrix && (
                <Card className="shadow-lg bg-white/90 backdrop-blur-sm mb-8">
                  <CardHeader className="bg-gradient-to-r from-sky-600 to-blue-600 text-white">
                    <CardTitle className="text-2xl font-bold">🌦️ Weather-Crop Compatibility Matrix</CardTitle>
                    <CardDescription className="text-sky-100">
                      Quick reference guide for optimal crop selection based on weather conditions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {Object.entries(weatherCropMatrix.weather_crop_matrix).map(([weather, crops]) => (
                        <div key={weather} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <h3 className="text-lg font-bold mb-3 capitalize flex items-center">
                            {weather === 'sunny' && '☀️'}
                            {weather === 'partly_cloudy' && '⛅'}
                            {weather === 'cloudy' && '☁️'}
                            {weather === 'rainy' && '🌧️'}
                            {weather === 'windy' && '💨'}
                            {weather === 'stormy' && '⛈️'}
                            <span className="ml-2">{weather.replace('_', ' ')}</span>
                          </h3>
                          
                          {crops.excellent_crops?.length > 0 && (
                            <div className="mb-3">
                              <h4 className="font-semibold text-green-700 mb-1">🌟 Excellent:</h4>
                              <div className="flex flex-wrap gap-1">
                                {crops.excellent_crops.map((crop, idx) => (
                                  <Badge key={idx} className="bg-green-100 text-green-800 border-green-300">
                                    {crop}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {crops.good_crops?.length > 0 && (
                            <div className="mb-3">
                              <h4 className="font-semibold text-yellow-700 mb-1">👍 Good:</h4>
                              <div className="flex flex-wrap gap-1">
                                {crops.good_crops.map((crop, idx) => (
                                  <Badge key={idx} className="bg-yellow-100 text-yellow-800 border-yellow-300">
                                    {crop}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {crops.poor_crops?.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-red-700 mb-1">⚠️ Challenging:</h4>
                              <div className="flex flex-wrap gap-1">
                                {crops.poor_crops.map((crop, idx) => (
                                  <Badge key={idx} className="bg-red-100 text-red-800 border-red-300">
                                    {crop}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">📋 Legend:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center">
                          <Badge className="bg-green-100 text-green-800 border-green-300 mr-2">🌟 Excellent</Badge>
                          <span className="text-green-700">Optimal growth conditions</span>
                        </div>
                        <div className="flex items-center">
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300 mr-2">👍 Good</Badge>
                          <span className="text-yellow-700">Suitable with proper care</span>
                        </div>
                        <div className="flex items-center">
                          <Badge className="bg-red-100 text-red-800 border-red-300 mr-2">⚠️ Challenging</Badge>
                          <span className="text-red-700">Requires extra protection</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Detailed Crop Weather Guides */}
              {cropWeatherGuides.length === 0 ? (
                <Card className="shadow-lg">
                  <CardContent className="p-8 text-center">
                    <p className="text-gray-500 text-lg">No detailed crop weather guides yet. Create your first guide by entering a crop name and clicking "Crop Weather Guide"!</p>
                  </CardContent>
                </Card>
              ) : (
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">📚 Detailed Crop Weather Guides</h3>
                  {cropWeatherGuides.map((guide, index) => (
                    <Card 
                      key={guide.id} 
                      className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-white/90 backdrop-blur-sm mb-6"
                      data-testid={`crop-guide-card-${index}`}
                    >
                      <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-xl font-bold">{guide.crop_name}</CardTitle>
                            <CardDescription className="text-indigo-100">
                              Complete weather compatibility guide
                            </CardDescription>
                          </div>
                          <div className="flex space-x-2">
                            {guide.optimal_weather.map((weather, idx) => (
                              <Badge key={idx} className="bg-white text-indigo-600 font-medium">
                                {weather === 'sunny' && '☀️'}
                                {weather === 'partly_cloudy' && '⛅'}
                                {weather === 'cloudy' && '☁️'}
                                {weather === 'rainy' && '🌧️'}
                                {weather.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-3 bg-blue-50 rounded-lg">
                              <h4 className="font-semibold text-blue-900 mb-1">🌡️ Temperature:</h4>
                              <p className="text-blue-800 text-sm">{guide.temperature_range}</p>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg">
                              <h4 className="font-semibold text-green-900 mb-1">🌧️ Rainfall:</h4>
                              <p className="text-green-800 text-sm">{guide.rainfall_requirement}</p>
                            </div>
                            <div className="p-3 bg-purple-50 rounded-lg">
                              <h4 className="font-semibold text-purple-900 mb-1">💧 Humidity:</h4>
                              <p className="text-purple-800 text-sm">{guide.humidity_preference}</p>
                            </div>
                            <div className="p-3 bg-orange-50 rounded-lg">
                              <h4 className="font-semibold text-orange-900 mb-1">📅 Season:</h4>
                              <p className="text-orange-800 text-sm">{guide.growing_season}</p>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">📖 Complete Weather Guide:</h4>
                            <div className="text-gray-700 bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
                                {guide.planting_tips}
                              </pre>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-500">
                            Guide generated on: {new Date(guide.created_at).toLocaleString()}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* AI Assistant Tab */}
          <TabsContent value="ai-assistant" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[600px]">
              
              {/* Chat Sessions Sidebar */}
              <div className="lg:col-span-1">
                <Card className="shadow-lg bg-white/90 backdrop-blur-sm h-full">
                  <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                    <CardTitle className="text-lg font-bold">🗂️ Chat Sessions</CardTitle>
                    <Button 
                      onClick={startNewChat}
                      size="sm"
                      className="bg-white text-blue-600 hover:bg-blue-50 mt-2"
                      data-testid="new-chat-btn"
                    >
                      + New Chat
                    </Button>
                  </CardHeader>
                  <CardContent className="p-4 overflow-y-auto h-[calc(100%-120px)]">
                    {chatSessions.length === 0 ? (
                      <p className="text-gray-500 text-sm">No previous chats. Start a new conversation!</p>
                    ) : (
                      <div className="space-y-2">
                        {chatSessions.map((session, index) => (
                          <div 
                            key={session.session_id}
                            onClick={() => loadSession(session.session_id)}
                            className={`p-3 rounded-lg cursor-pointer transition-colors ${ 
                              currentSessionId === session.session_id 
                                ? 'bg-blue-100 border border-blue-300' 
                                : 'bg-gray-50 hover:bg-gray-100'
                            }`}
                            data-testid={`chat-session-${index}`}
                          >
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {session.latest_user_message}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {session.message_count} messages
                              {session.farmer_name && ` • ${session.farmer_name}`}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(session.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Chat Interface */}
              <div className="lg:col-span-3">
                <Card className="shadow-lg bg-white/90 backdrop-blur-sm h-full">
                  <CardHeader className="bg-gradient-to-r from-emerald-600 to-green-600 text-white">
                    <CardTitle className="text-xl font-bold">🤖 Dr. AgroBot - Your AI Agriculture Assistant</CardTitle>
                    <CardDescription className="text-emerald-100">
                      Ask me anything about organic farming, crop management, pest control, and agricultural best practices!
                    </CardDescription>
                  </CardHeader>
                  
                  {/* Chat Messages */}
                  <div className="flex-1 p-4 overflow-y-auto h-[400px] bg-gradient-to-b from-green-50 to-emerald-50">
                    {chatMessages.length === 0 ? (
                      <div className="h-full flex items-center justify-center">
                        <div className="text-center max-w-md">
                          <div className="text-6xl mb-4">🌾</div>
                          <h3 className="text-xl font-semibold text-gray-800 mb-2">Welcome to Dr. AgroBot!</h3>
                          <p className="text-gray-600 mb-4">
                            I'm your AI agriculture assistant. Ask me about:
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="bg-white p-2 rounded-lg shadow-sm">🌱 Crop Management</div>
                            <div className="bg-white p-2 rounded-lg shadow-sm">🐛 Pest Control</div>
                            <div className="bg-white p-2 rounded-lg shadow-sm">🌿 Organic Methods</div>
                            <div className="bg-white p-2 rounded-lg shadow-sm">🌦️ Weather Planning</div>
                            <div className="bg-white p-2 rounded-lg shadow-sm">🧪 Soil Health</div>
                            <div className="bg-white p-2 rounded-lg shadow-sm">💰 Farm Business</div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {chatMessages.map((message, index) => (
                          <div key={message.id} className="space-y-3">
                            {/* User Message */}
                            <div className="flex justify-end">
                              <div className="bg-emerald-600 text-white p-3 rounded-lg max-w-md shadow-md">
                                <div className="text-sm font-medium">You:</div>
                                <div className="mt-1">{message.user_message}</div>
                              </div>
                            </div>
                            
                            {/* AI Response */}
                            <div className="flex justify-start">
                              <div className="bg-white p-4 rounded-lg max-w-2xl shadow-md border border-gray-200">
                                <div className="flex items-center mb-2">
                                  <div className="text-2xl mr-2">🤖</div>
                                  <div className="text-sm font-medium text-gray-800">Dr. AgroBot:</div>
                                </div>
                                <div className="text-gray-700 leading-relaxed">
                                  <pre className="whitespace-pre-wrap font-sans text-sm">
                                    {message.ai_response}
                                  </pre>
                                </div>
                                <div className="text-xs text-gray-400 mt-2">
                                  {new Date(message.created_at).toLocaleString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Chat Input */}
                  <CardContent className="p-4 border-t bg-white">
                    <div className="space-y-4">
                      <div className="flex space-x-2 items-start">
                        <div className="flex-1 flex items-start gap-2">
                          <Textarea
                            placeholder="Ask Dr. AgroBot: How do I control aphids organically? What's the best time to plant tomatoes? How can I improve my soil health?"
                            value={userMessage}
                            onChange={(e) => setUserMessage(e.target.value)}
                            className="flex-1 min-h-[60px] resize-none border-gray-300 focus:border-emerald-500"
                            data-testid="chat-input"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendChatMessage();
                              }
                            }}
                          />
                          <VoiceInputButton
                            language={selectedLanguage || 'en'}
                            onTranscript={(text) => setUserMessage(prev => prev ? prev + ' ' + text : text)}
                            placeholder={t('listening', 'Listening...')}
                          />
                        </div>
                        <Button 
                          onClick={sendChatMessage}
                          disabled={isChatLoading}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                          data-testid="send-chat-btn"
                        >
                          {isChatLoading ? 'Sending...' : 'Send 🚀'}
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setUserMessage('How do I identify and treat plant diseases organically?')}
                          data-testid="quick-question-diseases"
                        >
                          🦠 Plant Diseases
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setUserMessage('What are the best organic fertilizers for vegetables?')}
                          data-testid="quick-question-fertilizers"
                        >
                          🌿 Organic Fertilizers
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => setUserMessage('How should I plan my crop rotation for next season?')}
                          data-testid="quick-question-rotation"
                        >
                          🔄 Crop Rotation
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* About Page Tab */}
          <TabsContent value="about" className="space-y-8">
            <div className="max-w-4xl mx-auto px-6">
              {/* Page Header */}
              <div className="text-center mb-12">
                <h1 className="text-5xl font-bold text-gray-900 mb-4 font-serif">About Free Agro</h1>
                <div className="w-24 h-1 bg-emerald-600 mx-auto rounded-full"></div>
              </div>

              {/* Back to Home Button - Top */}
              <div className="text-center mb-8">
                <Button
                  onClick={() => setActiveTab('analyze')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium"
                  data-testid="back-to-home-top"
                >
                  ← Back to Home
                </Button>
              </div>

              {/* Section 1 - Introduction */}
              <section className="mb-12 bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-emerald-100">
                <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center">
                  <span className="text-4xl mr-3">🌱</span>
                  Reimagining Farming for a Smarter Future
                </h2>
                <div className="text-gray-700 text-lg leading-relaxed space-y-4">
                  <p>
                    Across the world, farmers face the same challenge: how to produce more with less — less water, 
                    less fertilizer, less labor, less time. At the same time, they're expected to protect the soil, 
                    reduce chemical use, and stay profitable in uncertain markets.
                  </p>
                  <p>
                    <strong>Free Agro</strong> was born to solve this challenge. It's more than an app — it's a 
                    digital partner for every farmer, turning complex data into simple, actionable advice.
                  </p>
                </div>
              </section>

              {/* Section 2 - Our Mission */}
              <section className="mb-12 bg-gradient-to-r from-emerald-50 to-green-50 p-8 rounded-xl border border-emerald-200">
                <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center">
                  <span className="text-4xl mr-3">🚀</span>
                  Our Mission
                </h2>
                <div className="text-gray-700 text-lg leading-relaxed space-y-4">
                  <p>
                    To empower farmers and agribusinesses with AI-driven intelligence that makes agriculture 
                    more profitable, sustainable, and resilient.
                  </p>
                  <p>
                    We're building a world where every farmer — whether smallholder or large-scale — can access 
                    the same powerful tools previously available only to corporations and researchers.
                  </p>
                </div>
              </section>

              {/* Section 3 - How It Works */}
              <section className="mb-12 bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-emerald-100">
                <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center">
                  <span className="text-4xl mr-3">🌾</span>
                  How Free Agro Works
                </h2>
                <div className="text-gray-700 text-lg leading-relaxed space-y-4">
                  <p>
                    Free Agro combines satellite data, weather forecasts, soil insights, and AI predictions into 
                    one easy-to-use dashboard. Instead of juggling spreadsheets, apps, and guesswork, farmers 
                    see clear recommendations for:
                  </p>
                  <ul className="list-disc list-inside ml-6 space-y-2">
                    <li>Planting and harvesting schedules</li>
                    <li>Smart irrigation and fertilizer use</li>
                    <li>Pest and disease early warnings</li>
                    <li>Yield forecasting and market planning</li>
                  </ul>
                  <p>
                    All of this is powered by our agent-based AI platform, built on <strong>Emergent</strong>. 
                    This means Free Agro can evolve quickly — adding new modules, new crops, or new languages — 
                    simply by telling it what farmers need.
                  </p>
                </div>
              </section>

              {/* Section 4 - What Makes Us Different */}
              <section className="mb-12 bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-xl border border-green-200">
                <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center">
                  <span className="text-4xl mr-3">💡</span>
                  What Makes Free Agro Different
                </h2>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100">
                      <h3 className="font-bold text-emerald-600 mb-3">🧠 AI That Speaks Farmer</h3>
                      <p className="text-gray-700">
                        You don't need to be a data scientist. Free Agro translates complex analytics into 
                        simple, actionable steps.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100">
                      <h3 className="font-bold text-emerald-600 mb-3">💰 Precision Without the Price Tag</h3>
                      <p className="text-gray-700">
                        Tools once reserved for industrial farms are now affordable and accessible to everyone.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100">
                      <h3 className="font-bold text-emerald-600 mb-3">⚡ Real-Time, Real Results</h3>
                      <p className="text-gray-700">
                        Get instant updates on crop health, weather shifts, or risks so you can act before it's too late.
                      </p>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100">
                      <h3 className="font-bold text-emerald-600 mb-3">🌿 Sustainability at the Core</h3>
                      <p className="text-gray-700">
                        Reduce water, fertilizer, and chemical use while boosting yields and protecting the environment.
                      </p>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-emerald-100 text-center">
                    <h3 className="font-bold text-emerald-600 mb-3">📈 Designed to Grow With You</h3>
                    <p className="text-gray-700">
                      Whether you manage one acre or thousands, Free Agro scales to your needs.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 5 - Vision for Future */}
              <section className="mb-12 bg-white/80 backdrop-blur-sm p-8 rounded-xl shadow-lg border border-emerald-100">
                <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center">
                  <span className="text-4xl mr-3">🌍</span>
                  Our Vision for the Future
                </h2>
                <div className="text-gray-700 text-lg leading-relaxed space-y-4">
                  <p>
                    We see a future where every farm is a smart farm — where farmers don't guess but know, 
                    where decisions are based on data, not hope.
                  </p>
                  <p className="font-semibold text-emerald-600">Coming soon to Free Agro:</p>
                  <ul className="list-disc list-inside ml-6 space-y-2">
                    <li>🚁 Drone and sensor integrations for real-time field data</li>
                    <li>👨‍🌾 AI agronomy assistants for instant expert advice</li>
                    <li>🌏 Localized modules tailored to your soil, your crop, your language</li>
                    <li>👥 Community features to connect farmers, agronomists, and buyers</li>
                  </ul>
                </div>
              </section>

              {/* Section 6 - Our Promise */}
              <section className="mb-12 bg-gradient-to-r from-emerald-600 to-green-600 text-white p-8 rounded-xl shadow-xl">
                <h2 className="text-3xl font-bold mb-6 flex items-center">
                  <span className="text-4xl mr-3">🤝</span>
                  Our Promise
                </h2>
                <div className="text-lg leading-relaxed space-y-4">
                  <p>
                    Free Agro isn't just software. It's a commitment to farmers — to make agriculture smarter, 
                    fairer, and more sustainable. We believe technology should serve the hands that feed the world.
                  </p>
                  <p className="text-xl font-semibold">
                    With Free Agro, you're not just adopting a tool. You're embracing the future of farming.
                  </p>
                </div>
              </section>

              {/* Back to Home Button - Bottom */}
              <div className="text-center mb-8">
                <Button
                  onClick={() => setActiveTab('analyze')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-medium text-lg"
                  data-testid="back-to-home-bottom"
                >
                  ← Back to Home
                </Button>
              </div>

              {/* Footer */}
              <div className="text-center py-8 border-t border-emerald-200">
                <p className="text-gray-600 text-lg">
                  <span className="text-emerald-600 font-semibold">Free Agro</span> - 
                  Empowering farmers with AI-driven agricultural intelligence
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Built with 🌱 for sustainable agriculture
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Organic Farming Guide Tab */}
          <TabsContent value="organic-guide" className="space-y-6">
            <div className="max-w-5xl mx-auto px-6">
              <div className="text-center mb-12">
                <h1 className="text-5xl font-bold text-emerald-700 mb-4">📖 Complete Guide to Organic Farming</h1>
                <p className="text-xl text-gray-600">जैविक खेती की संपूर्ण गाइड</p>
                <p className="text-gray-500 mt-2">Step-by-step processes, recipes, and solutions for sustainable farming</p>
              </div>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="🔍 Search guide... (e.g., 'Jeevamrut', 'pest control', 'compost')"
                    value={guideSearchQuery}
                    onChange={(e) => setGuideSearchQuery(e.target.value)}
                    className="w-full pl-4 pr-10 py-3 text-lg border-2 border-emerald-300 focus:border-emerald-500 rounded-lg"
                  />
                  {guideSearchQuery && (
                    <button
                      onClick={() => setGuideSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {guideSearchQuery && (
                  <p className="text-sm text-gray-600 mt-2 text-center">
                    Searching for: <span className="font-semibold text-emerald-700">{guideSearchQuery}</span>
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 justify-center mb-8">
                <Button 
                  variant="outline" 
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  onClick={handlePrintGuide}
                >
                  🖨️ Print Guide
                </Button>
                <Button 
                  variant="outline" 
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  onClick={handleDownloadPDF}
                >
                  📥 Download PDF
                </Button>
              </div>

              {/* Guide Content - Wrapped in ref for PDF generation */}
              <div ref={guideContentRef}>

              {/* Section 1: Small-Scale Organic Farming */}
              {shouldShowSection('small scale organic farming site soil assessment planning crop selection compost rotation biodiversity pest disease fertilizer monitoring') && (
              <CollapsibleSection 
                title="Step-by-Step Process: Small-Scale Organic Farming" 
                icon="🌾"
                defaultOpen={true}
              >
                <div className="space-y-4">
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">1. Site and Soil Assessment</h4>
                    <ul className="list-disc ml-6 space-y-1 text-gray-700">
                      <li>Test your soil for organic matter, pH, and microbial activity</li>
                      <li>Assess water availability and climate for suitable crop selection</li>
                    </ul>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">2. Planning and Crop Selection</h4>
                    <p className="text-gray-700">Choose crops suited to your local conditions (e.g., hardy vegetables like tomatoes, lettuce)</p>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">3. Organic Inputs and Composting</h4>
                    <p className="text-gray-700">Start a composting system using kitchen waste, manure, and green waste. Layer materials, turn regularly, and keep moist. Compost is ready in 2–3 months.</p>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">4. Crop Rotation and Biodiversity</h4>
                    <p className="text-gray-700">Plan crop rotations to improve soil health and reduce pests. For example, follow a nitrogen-heavy crop (like corn) with nitrogen-fixing plants (like beans).</p>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">5. Natural Pest and Disease Management</h4>
                    <p className="text-gray-700">Use Integrated Pest Management (IPM): encourage beneficial insects (ladybugs), use neem oil, and physical barriers. Remove affected plants and rotate crops to minimize disease.</p>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">6. Organic Fertilizers</h4>
                    <p className="text-gray-700">Apply compost, manure, or homemade biofertilizers to maintain fertility.</p>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-2">7. Ongoing Monitoring</h4>
                    <p className="text-gray-700">Continuously observe soil, crop, and pest status to respond with needed organic interventions.</p>
                  </div>
                </div>
              </CollapsibleSection>
              )}

              {/* Section 2: Large-Scale Organic Farming */}
              {shouldShowSection('large scale organic farming assessment transition planning infrastructure input sourcing pest disease management production monitoring evaluation market') && (
              <CollapsibleSection 
                title="Step-by-Step Process: Large-Scale Organic Farming" 
                icon="🚜"
              >
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">1. Detailed Site Assessment</h4>
                    <p className="text-gray-700">Comprehensive soil, water, and environmental analysis. Digital mapping and technology tools assist with large areas.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">2. Transition Planning</h4>
                    <p className="text-gray-700">Gradually reduce synthetic inputs over 2–3 years to achieve certification standards for organic production.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">3. Infrastructure and Input Sourcing</h4>
                    <p className="text-gray-700">Invest in irrigation systems, storage, composting units, and secure organic seeds, biofertilizers, and biopesticides. All inputs must be certified organic.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">4. Farm Planning</h4>
                    <p className="text-gray-700">Develop crop rotation schedules, integration of livestock, biogas plants, and multi-cropping systems for biodiversity. Keep detailed records for certification.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">5. Advanced Pest and Disease Management</h4>
                    <p className="text-gray-700">Apply IPM at scale: deploy beneficial insects, install bird perches, use biological sprays, and incorporate pest-confusing planting patterns.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">6. Organic Input Production</h4>
                    <p className="text-gray-700">Set up large compost pits/windrows and prepare bio-fertilizers like Jeevamrut or Panchagavya.</p>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-2">7. Monitoring, Evaluation, Market Linkage</h4>
                    <p className="text-gray-700">Use tech for monitoring soil and crop health, pursue certification, and connect with organic markets.</p>
                  </div>
                </div>
              </CollapsibleSection>
              )}

              {/* Section 3: Essential Organic Materials */}
              {shouldShowSection('essential organic materials compost jeevamrut panchagavya neem oil mulch') && (
              <CollapsibleSection 
                title="Essential Organic Materials & How to Make Them" 
                icon="🧪"
              >
                <div className="space-y-6">
                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-bold text-gray-800 mb-2">Compost</h4>
                    <p className="text-gray-700">Decompose green waste, dry leaves, manure, and kitchen scraps in a heap or pit. Maintain moisture and aerate weekly for 2–3 months.</p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-bold text-gray-800 mb-2">Jeevamrut (per acre)</h4>
                    <p className="text-gray-700 mb-2"><strong>Ingredients:</strong> 10 kg cow dung, 10 L cow urine, 2 kg jaggery, 2 kg pulse flour, 1 kg local soil, 200 L water</p>
                    <p className="text-gray-700"><strong>Method:</strong> Mix all in a drum, stir daily, ferment for 7 days, and apply to soil or crops.</p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-bold text-gray-800 mb-2">Panchagavya</h4>
                    <p className="text-gray-700 mb-2"><strong>Ingredients:</strong> Cow dung, cow urine, milk, curd, and ghee in specified ratios</p>
                    <p className="text-gray-700"><strong>Method:</strong> Ferment 15 days, stirring occasionally. Use as foliar spray or soil drench for plant growth promotion.</p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-bold text-gray-800 mb-2">Neem Oil Spray</h4>
                    <p className="text-gray-700">Combine neem seed oil with soap and water and spray to control pests.</p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h4 className="font-bold text-gray-800 mb-2">Mulch</h4>
                    <p className="text-gray-700">Use straw, grass, leaves, or hulls to cover soil, conserving moisture, improving fertility, and suppressing weeds.</p>
                  </div>
                </div>
              </CollapsibleSection>
              )}

              {/* Section 4: Tools and Equipment */}
              {shouldShowSection('tools equipment broadfork wheel hoe harvest knives silage tarps watering cans drip lines tractor rotavator compost turner irrigation digital mapping') && (
              <CollapsibleSection 
                title="Tools and Equipment" 
                icon="🔧"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">🌿</span> Small-Scale Tools
                    </h4>
                    <ul className="list-disc ml-6 space-y-2 text-gray-700">
                      <li>Broadfork</li>
                      <li>Wheel hoe</li>
                      <li>Harvest knives</li>
                      <li>Silage tarps</li>
                      <li>Watering cans or drip lines</li>
                    </ul>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-4 flex items-center">
                      <span className="mr-2">🚜</span> Large-Scale Equipment
                    </h4>
                    <ul className="list-disc ml-6 space-y-2 text-gray-700">
                      <li>Tractors</li>
                      <li>Rotavators</li>
                      <li>Compost turners</li>
                      <li>Irrigation systems</li>
                      <li>Digital mapping tools</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>
              )}

              {/* Section 5: Key Organic Solutions - Part 1 */}
              {shouldShowSection('key organic solutions recipes jeevamrut panchagavya neemastra neem oil hot pepper caterpillar beetle pest control insecticide') && (
              <CollapsibleSection 
                title="Key Organic Solutions and Recipes (Part 1)" 
                icon="🌿"
              >
                <div className="space-y-6">
                  {/* Recipe 1: Jeevamrut */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
                    <h4 className="text-xl font-bold text-green-800 mb-3">1. Jeevamrut 🧪</h4>
                    <div className="mb-3">
                      <span className="inline-block bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Purpose: Microbial bio-fertilizer
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Ingredients:</p>
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        <li>10 kg cow dung</li>
                        <li>10 L cow urine</li>
                        <li>2 kg jaggery</li>
                        <li>2 kg pulse flour</li>
                        <li>1 kg local soil</li>
                        <li>200 L water</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold text-gray-800 mb-2">Method:</p>
                      <p className="text-gray-700">Mix all ingredients in a drum, stir daily, ferment for 48 hours. Apply as soil drench or dilute for foliar spray.</p>
                    </div>
                  </div>

                  {/* Recipe 2: Panchagavya */}
                  <div className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-xl border-2 border-yellow-200">
                    <h4 className="text-xl font-bold text-yellow-800 mb-3">2. Panchagavya 🐄</h4>
                    <div className="mb-3">
                      <span className="inline-block bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Purpose: Growth promoter, disease resistance
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Ingredients:</p>
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        <li>5 kg cow dung</li>
                        <li>3 L cow urine</li>
                        <li>2 L milk</li>
                        <li>2 L curd</li>
                        <li>1 kg ghee</li>
                        <li>3 L sugarcane juice</li>
                        <li>3 L tender coconut water</li>
                        <li>12 bananas (mashed)</li>
                        <li>2 L toddy/grape juice</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold text-gray-800 mb-2">Method:</p>
                      <p className="text-gray-700">Mix dung and ghee, ferment 3 days. Add remaining ingredients, ferment 15 days, stirring daily. Dilute 3–4 L per 100 L water for foliar application.</p>
                    </div>
                  </div>

                  {/* Recipe 3: Neemastra */}
                  <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
                    <h4 className="text-xl font-bold text-blue-800 mb-3">3. Neemastra 🌿</h4>
                    <div className="mb-3">
                      <span className="inline-block bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Purpose: Insecticide for pest control
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Ingredients:</p>
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        <li>200 L water</li>
                        <li>5–10 L cow urine</li>
                        <li>2 kg cow dung</li>
                        <li>10 kg crushed neem leaves</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold text-gray-800 mb-2">Method:</p>
                      <p className="text-gray-700">Mix thoroughly, ferment 48 hours (stir twice daily), filter, spray directly. No dilution required. Effective against aphids, beetles, and caterpillars.</p>
                    </div>
                  </div>

                  {/* Recipe 4: Neem Oil Spray */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-2 border-purple-200">
                    <h4 className="text-xl font-bold text-purple-800 mb-3">4. Neem Oil Spray 💧</h4>
                    <div className="mb-3">
                      <span className="inline-block bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Purpose: General insecticide
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Ingredients:</p>
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        <li>2–4 tbsp neem oil</li>
                        <li>1 gallon water</li>
                        <li>Mild soap</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold text-gray-800 mb-2">Method:</p>
                      <p className="text-gray-700">Mix well, shake thoroughly, spray on plants. Repeat every 7–14 days or after rain. Especially effective for sucking pests.</p>
                    </div>
                  </div>

                  {/* Recipe 5: Hot Pepper Solution */}
                  <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-xl border-2 border-red-200">
                    <h4 className="text-xl font-bold text-red-800 mb-3">5. Hot Pepper Solution 🌶️</h4>
                    <div className="mb-3">
                      <span className="inline-block bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold mb-2">
                        Purpose: Deterrent for caterpillars, beetles, ants
                      </span>
                    </div>
                    <div className="bg-white p-4 rounded-lg mb-3">
                      <p className="font-semibold text-gray-800 mb-2">Ingredients:</p>
                      <ul className="list-disc ml-6 text-gray-700 space-y-1">
                        <li>15 finely chopped hot peppers</li>
                        <li>1 L water</li>
                      </ul>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <p className="font-semibold text-gray-800 mb-2">Method:</p>
                      <p className="text-gray-700">Steep peppers in water for 24 hours, strain, and spray on crops.</p>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>
              )}

              {/* Section 6: Benefits */}
              {shouldShowSection('benefits support organic farming soil fertility growth yield pest disease management sustainability') && (
              <CollapsibleSection 
                title="How These Solutions Support Organic Farming" 
                icon="✅"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="font-bold text-green-800 mb-3 flex items-center text-lg">
                      <span className="mr-2">🌱</span> Soil Fertility
                    </h4>
                    <p className="text-gray-700">Jeevamrut, Panchagavya, Sanjeevak, and compost tea add nutrients and beneficial microbes to enhance soil health.</p>
                  </div>

                  <div className="bg-yellow-50 p-6 rounded-lg">
                    <h4 className="font-bold text-yellow-800 mb-3 flex items-center text-lg">
                      <span className="mr-2">📈</span> Growth and Yield
                    </h4>
                    <p className="text-gray-700">Panchagavya and Dasagavya enhance crop growth, vigor, and quality, leading to better yields.</p>
                  </div>

                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="font-bold text-blue-800 mb-3 flex items-center text-lg">
                      <span className="mr-2">🦗</span> Pest/Disease Management
                    </h4>
                    <p className="text-gray-700">Neemastra, neem oil, hot pepper, garlic/onion brews, and Bordeaux control pests while minimizing toxic residue.</p>
                  </div>

                  <div className="bg-emerald-50 p-6 rounded-lg">
                    <h4 className="font-bold text-emerald-800 mb-3 flex items-center text-lg">
                      <span className="mr-2">♻️</span> Sustainability
                    </h4>
                    <p className="text-gray-700">All recipes use on-farm, renewable/biodegradable resources, minimizing harmful environmental impacts.</p>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-emerald-600 to-green-600 text-white p-6 rounded-xl">
                  <p className="text-lg italic">
                    "These solutions are integral to both small and large-scale organic farming systems. Their preparation and use promote an ecological cycle, limiting off-farm input costs and supporting healthier plants and soils."
                  </p>
                </div>
              </CollapsibleSection>
              )}

              {/* No Results Message */}
              {guideSearchQuery && !shouldShowAnySection() && (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🔍</div>
                  <h3 className="text-2xl font-bold text-gray-700 mb-2">No results found</h3>
                  <p className="text-gray-600">Try searching for: Jeevamrut, Panchagavya, Neemastra, compost, pest control, tools</p>
                  <Button 
                    onClick={() => setGuideSearchQuery('')}
                    className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Clear Search
                  </Button>
                </div>
              )}
              
              </div> {/* Close ref div */}

              {/* Back to Home Button */}
              <div className="flex justify-center mt-12">
                <Button 
                  onClick={() => setActiveTab('analyze')}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg"
                >
                  ← Back to Home
                </Button>
              </div>
            </div>
          </TabsContent>

        </Tabs>
      </div>
      
      {/* Footer with Legal Notice */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column - About */}
            <div>
              <h3 className="text-2xl font-bold text-emerald-400 mb-3">🌾 Free Agro</h3>
              <p className="text-gray-300 text-sm mb-4">
                AI-powered organic farming solutions for Indian farmers. 
                Empowering agriculture with technology and tradition.
              </p>
              <p className="text-gray-400 text-xs">
                Supporting 8 Indian languages | Open-access platform
              </p>
            </div>
          </div>
          
          {/* Bottom Bar with Minimal Legal Notice */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="text-gray-400 text-xs text-center mb-2">
              © 2025 Free Agro. All rights reserved. | Made for Indian Farmers
            </p>
            <p className="text-gray-500 text-xs text-center">
              We cannot diagnose restricted plants under Indian law. See{' '}
              <button 
                onClick={() => setShowFullLegalPolicy(!showFullLegalPolicy)}
                className="underline hover:text-gray-400"
              >
                terms
              </button>
              .
            </p>
          </div>
        </div>
      </footer>
      
      {/* Terms & Conditions Modal */}
      {showFullLegalPolicy && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowFullLegalPolicy(false)}>
          <div className="bg-white rounded-xl max-w-2xl max-h-[80vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Terms & Conditions</h2>
              <button onClick={() => setShowFullLegalPolicy(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            
            <div className="space-y-4 text-gray-700 text-sm">
              <p className="text-gray-600 italic">Last updated: January 2025</p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">1. Acceptance of Terms</h3>
              <p>
                By accessing and using Free Agro, you accept and agree to be bound by these terms and conditions.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">2. Service Description</h3>
              <p>
                Free Agro provides AI-powered plant disease diagnosis and organic farming solutions for legal agricultural plants only.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">3. Restricted Plants Policy</h3>
              <p>
                <span className="font-semibold">Free Agro cannot and will not provide diagnosis for plants that are illegal or restricted under Indian law.</span> This includes but is not limited to: Cannabis, Opium poppy, Coca plant, Peyote, Magic mushrooms, Kratom, Datura, and Belladonna. Images of such plants will be rejected without explanation. We comply with the Narcotic Drugs and Psychotropic Substances Act, 1985, and all applicable Indian laws.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">4. User Responsibilities</h3>
              <p>
                Users agree to upload only images of legal agricultural or garden plants. Users are responsible for ensuring compliance with all local, state, and national laws regarding plant cultivation and ownership.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">5. Limitation of Liability</h3>
              <p>
                Free Agro provides information for educational purposes only. We are not responsible for any actions taken based on our recommendations. Always consult with local agricultural experts.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">6. Privacy</h3>
              <p>
                We do not store personal information. Uploaded images are processed temporarily and deleted immediately after analysis.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-6">7. Changes to Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. Continued use of the service constitutes acceptance of updated terms.
              </p>
            </div>
            
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button 
                onClick={() => setShowFullLegalPolicy(false)}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-lg font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Toaster />
        </>
      )}
    </div>
  );
}

export default App;
